import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { EMPTY_TICKET_FILTERS, saveCurrentFilters, type TicketFilters } from '../api/easeedesk'
import {
  ticketsDashboardQuery,
  type BoardAged,
  type BoardCell,
  type BoardCentre,
  type BoardRow,
  type BoardTileFilters,
} from '../api/easeedeskBoards'
import { hrefOf } from '../overview/href'
import { Content } from '../ui/Shell'
import {
  AGE_BOX_STYLE,
  EMPTY_LABEL_STYLE,
  VALUE_BOX_STYLE,
  WALL_BACKGROUND,
  ageTileStyle,
  BOARD_RANGES,
  centreHeadInnerStyle,
  centreHeadStyle,
  clockText,
  rangeOf,
  rowLabelStyle,
  summaryBoxStyle,
  summaryLabelStyle,
  summaryValueStyle,
  valueTileStyle,
  type BoardRangeName,
} from './board'

/**
 * Tabloul de tichete - `Easeedesk::tickets_dashboard`, macheta
 * `views/version-3/dashboards/easeedesk/tickets_dashboard/index.php` plus
 * casutele pe care le desena `DashboardTickets` din
 * `public/js/dashboards/easeedesk.js`.
 *
 * Acelasi perete: noua centre in cap, opt randuri de cifre, randul de medii
 * si randul de activitate. Aceleasi culori, aceleasi inaltimi, aceleasi
 * etichete. Cifrele vin dintr-o singura cerere, nu din unsprezece.
 *
 * Apasarea unei casute pregateste si filtrele listei, ca in ecranul vechi:
 * intai se scriu (`POST /easeedesk/user-filters`), abia apoi se deschide lista
 * - care si le citeste o data, la deschidere. In portal cele doua plecau
 * DEODATA (`onclick` pe casuta, `<a target="_blank">` inauntru), deci uneori
 * lista apuca sa se incarce inaintea filtrelor si se deschidea nefiltrata.
 * Aici fila noua se deschide goala si se trimite mai departe dupa ce s-a
 * scris, deci nu mai e nicio intrecere.
 */

/** Filtrele casutei, in forma pe care o tine minte lista. */
export function filtersOfTile(tile: BoardTileFilters): TicketFilters {
  return {
    ...EMPTY_TICKET_FILTERS,
    type: tile.type.map(String),
    status: tile.status.map(String),
    group: tile.group.map(String),
    advanced: tile.advanced,
    filterFrom: tile.filterFrom,
  }
}

/**
 * Deschide lista cu filtrele casutei: fila noua se cere INAINTE de scriere
 * (altfel o opreste browserul, fiindca n-ar mai fi pornita de apasare), si
 * primeste adresa abia dupa ce filtrele au ajuns la server.
 *
 * Daca fila a fost oprita totusi, se merge in fila de fata: mai bine lista
 * filtrata in locul tabloului decat o apasare care nu face nimic.
 */
async function openFiltered(list: string, tile: BoardTileFilters): Promise<void> {
  const tab = window.open('', '_blank')
  try {
    await saveCurrentFilters(filtersOfTile(tile))
  } catch {
    /* Filtrele n-au putut fi scrise; lista se deschide asa cum era. */
  }
  if (tab) {
    tab.location.href = list
  } else {
    window.location.href = list
  }
}

function Tile({
  cell,
  color,
  tall,
}: {
  cell: BoardCell | undefined
  color: string
  tall: boolean
}) {
  const list = hrefOf('easeedesk/tickets')
  const count = cell?.count ?? 0
  const age = cell?.age ?? ''
  const ticket = cell?.ticket ?? 0
  const filters = cell?.filters
  const value = <div style={valueTileStyle(color, tall)}>{count}</div>
  const aged = <div style={ageTileStyle(tall)}>{age}</div>
  return (
    <>
      <div style={VALUE_BOX_STYLE}>
        {list ? (
          <a
            href={list}
            target="_blank"
            rel="noreferrer"
            title="Open the ticket list filtered like this box"
            onClick={
              filters
                ? (e) => {
                    e.preventDefault()
                    void openFiltered(list, filters)
                  }
                : undefined
            }
          >
            {value}
          </a>
        ) : (
          value
        )}
      </div>
      <div style={AGE_BOX_STYLE}>
        {ticket > 0 ? (
          <a href={`${hrefOf('easeedesk/ticket') ?? '/easeedesk/ticket'}/${ticket}`} target="_blank" rel="noreferrer">
            {aged}
          </a>
        ) : (
          aged
        )}
      </div>
    </>
  )
}

function NumberRow({
  row,
  centers,
  cells,
}: {
  row: BoardRow
  centers: BoardCentre[]
  cells: Record<string, BoardCell> | undefined
}) {
  /* "Late Resolved" e randul inalt, ca in JS. */
  const tall = row.key === 'problems'
  return (
    <>
      <div style={rowLabelStyle(row.color, tall)}>
        <b>{row.label}</b>
      </div>
      <div className="row" style={{ backgroundColor: WALL_BACKGROUND }}>
        {centers.map((centre) => (
          <Tile key={centre.key} cell={cells?.[centre.key]} color={centre.color} tall={tall} />
        ))}
      </div>
    </>
  )
}

/** Casuta de doua etaje din randul de medii. */
function Summary({
  value,
  label,
  color,
  last,
  labelHeight,
  labelPadding,
}: {
  value: string
  label: string
  color: string
  last?: boolean
  labelHeight?: string
  labelPadding?: string
}) {
  return (
    <div style={summaryBoxStyle(!!last)}>
      <div style={summaryValueStyle(color)}>{value}</div>
      <div
        style={summaryLabelStyle(color, labelHeight ?? '50px', labelPadding)}
        dangerouslySetInnerHTML={{ __html: label }}
      />
    </div>
  )
}

/** Casuta de trei etaje: cat sta, ce e, si care tichet. */
function Oldest({ aged, label }: { aged: BoardAged | undefined; label: string }) {
  const ticket = aged?.ticket ?? 0
  const inner = (
    <>
      <div style={summaryValueStyle('#0066FF')}>{aged?.age ?? '0s'}</div>
      <div style={summaryLabelStyle('#0066FF', '40px', '15px')}>{label}</div>
      <div style={summaryValueStyle('#0066FF')}>{ticket}</div>
    </>
  )
  const href = ticket > 0 ? `${hrefOf('easeedesk/ticket') ?? '/easeedesk/ticket'}/${ticket}` : null
  return (
    <div style={summaryBoxStyle(false)}>
      {href ? (
        <a href={href} target="_blank" rel="noreferrer">
          {inner}
        </a>
      ) : (
        inner
      )}
    </div>
  )
}

function asAged(value: string | BoardAged | undefined): BoardAged | undefined {
  return value && typeof value === 'object' ? value : undefined
}

function asText(value: string | BoardAged | undefined): string {
  return typeof value === 'string' ? value : ''
}

export function TicketsDashboardPage() {
  const [rangeName, setRangeName] = useState<BoardRangeName>('Today')
  const range = rangeOf(rangeName)
  const q = useQuery(ticketsDashboardQuery(range.from, range.to))
  const [clock, setClock] = useState(clockText)

  useEffect(() => {
    const id = window.setInterval(() => setClock(clockText()), 1000)
    return () => window.clearInterval(id)
  }, [])

  const board = q.data
  const centers = board?.centers ?? []
  const averages = board?.averages ?? {}
  const activity = board?.activity ?? {}

  return (
    <Content>
      <div id="row" style={{ marginTop: '-20px' }}>
        <h1 style={{ fontSize: '20px', textAlign: 'center', width: '100%' }}>
          <img src="/td/images/logo/logo.png" style={{ height: 'auto', margin: 'auto', display: 'block' }} alt="" />
          <br />
          <b style={{ color: '#57636F' }}>Results and Activities EU Services Delivery Team </b>
          {/*
            Casuta de perioada din coltul din stanga sus, la locul si pe
            culorile machetei vechi (`.reportrange`). Acolo e un calendar cu
            sase scurtaturi; aici sunt scurtaturile, fiindca `daterangepicker`
            nu e in v4. Fara ea, tabloul arata ZIUA CURENTA si atat, desi
            capatul stie de mult sa primeasca `?from=&to=`.
          */}
          <div className="pull-left" style={{ fontSize: 12, marginLeft: -18, marginTop: -40, width: '11.5%', position: 'absolute' }}>
            <div className="reportrange" style={{ padding: 8, color: '#FFF', background: '#616F7B', height: 40 }}>
              <i className="fas fa-calendar" aria-hidden="true"></i>&nbsp;
              <select
                aria-label="Date range"
                value={rangeName}
                onChange={(e) => setRangeName(e.target.value as BoardRangeName)}
                style={{
                  textTransform: 'uppercase',
                  color: '#FFF',
                  background: 'transparent',
                  border: 0,
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                {BOARD_RANGES.map((name) => (
                  <option key={name} value={name} style={{ color: '#000' }}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </h1>
      </div>

      <div
        style={{
          float: 'left',
          width: '9%',
          textAlign: 'center',
          fontSize: '20px',
          height: '70px',
          marginTop: '5px',
          marginLeft: '-8px',
          marginRight: '1px',
          color: 'black',
          lineHeight: '25px',
        }}
      >
        <span style={{ fontWeight: 'bold' }}>{clock}</span>
        <br />
        <span style={{ fontSize: '18px' }}>Last refresh</span>
        <div style={{ fontWeight: 'bold' }}>{board?.elapsed ?? ''}</div>
      </div>

      <div className="row" style={{ backgroundColor: WALL_BACKGROUND }}>
        {centers.map((centre, i) => (
          <div key={centre.key} style={centreHeadStyle(i === 0)}>
            <div style={centreHeadInnerStyle(centre.color)}>
              {centre.label} <br /> Center
            </div>
          </div>
        ))}
      </div>

      {board && !board.ready ? (
        <div className="alert alert-warning" style={{ clear: 'both' }}>
          <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> The tickets dashboard tables are not
          installed on this database, so there are no numbers to show.
        </div>
      ) : null}

      {(board?.rows ?? []).map((row) => (
        <NumberRow key={row.key} row={row} centers={centers} cells={board?.cells?.[row.key]} />
      ))}

      <div style={EMPTY_LABEL_STYLE}></div>
      <div className="row" style={{ backgroundColor: WALL_BACKGROUND }}>
        <Summary value={averages.online_manager ?? ''} label="Active <br />Managers" color="#6c8296" />
        <Summary value={averages.online_technician ?? ''} label=" Active Technicians" color="#6c8296" />
        <Summary value={averages.resolved_SR_per_technician ?? ''} label="Invalid <br />Tickets" color="#0066FF" />
        <Summary value={averages.average_SR_resolved ?? ''} label="Average Time <br/> Resolve" color="#0066FF" />
        <Summary value={averages.average_connected ?? ''} label="Average Time <br />Connected" color="#0066FF" />
        <Summary value={averages.average_InProgress ?? ''} label="Average Time In <br/> Progress" color="#0066FF" />
        <Summary value={averages.average_unassigned ?? ''} label="Average Time to<br/> Take Over" color="#0066FF" last />
      </div>

      <div style={EMPTY_LABEL_STYLE}></div>
      <div className="row" style={{ backgroundColor: WALL_BACKGROUND }}>
        <Summary
          value={asText(activity.manager_last_activity)}
          label="Managers Longest<br /> time connected"
          color="#6c8296"
          labelHeight="80px"
        />
        <Summary
          value={asText(activity.technician_last_activity)}
          label="Technicians Longest<br /> time connected"
          color="#6c8296"
          labelHeight="80px"
        />
        <Oldest aged={asAged(activity.old_sr)} label="Longest Resolved" />
        <Oldest aged={asAged(activity.old_sr_assignee)} label="Oldest Ticket" />
        <Oldest aged={asAged(activity.max_connect_time)} label="Oldest Connected" />
        <Oldest aged={asAged(activity.max_in_progress)} label="Oldest In Progress" />
        <Summary
          value={asText(activity.escalated_sr_customer)}
          label="Customer Escalated"
          color="#0066FF"
          labelHeight="80px"
          labelPadding="10px"
          last
        />
      </div>
    </Content>
  )
}
